import api from "../api/axios";
import ENDPOINTS from "../api/endpoints";
import { formatDateForApi } from "../utils/formatDate";

const normalizeRule = (rule) => ({
  ...rule,
  date: formatDateForApi(rule?.date),
  startTime: rule?.startTime ? String(rule.startTime).slice(0, 5) : "",
  endTime: rule?.endTime ? String(rule.endTime).slice(0, 5) : "",
  isActive: rule?.isActive !== false,
});

const normalizeRuleListResponse = (data) => ({
  ...data,
  availabilityRuleList: Array.isArray(data?.availabilityRuleList)
    ? data.availabilityRuleList
        .map(normalizeRule)
        .sort((a, b) => {
          const dateCompare = String(a.date).localeCompare(String(b.date));
          if (dateCompare !== 0) return dateCompare;
          return String(a.startTime).localeCompare(String(b.startTime));
        })
    : [],
});

export const getAllAvailabilityRules = async () => {
  const response = await api.get(ENDPOINTS.AVAILABILITY.ALL);
  return normalizeRuleListResponse(response.data);
};

export const getAvailabilityRules = getAllAvailabilityRules;
export const getAvailabilities = getAllAvailabilityRules;

export const getAvailabilityRuleById = async (id) => {
  const response = await api.get(ENDPOINTS.AVAILABILITY.BY_ID(id));
  const data = response.data;

  return {
    ...data,
    availabilityRule: data?.availabilityRule
      ? normalizeRule(data.availabilityRule)
      : data?.availabilityRule,
  };
};

export const getAvailabilityByDate = async (date) => {
  const response = await api.get(
    ENDPOINTS.AVAILABILITY.BY_DATE(formatDateForApi(date))
  );
  return normalizeRuleListResponse(response.data);
};

export const getAvailabilityBetweenDates = async (start, end) => {
  const response = await api.get(ENDPOINTS.AVAILABILITY.BETWEEN, {
    params: {
      start: formatDateForApi(start),
      end: formatDateForApi(end),
    },
  });

  return normalizeRuleListResponse(response.data);
};

export const validateAvailability = async (date, start, end) => {
  const response = await api.get(ENDPOINTS.AVAILABILITY.VALIDATE, {
    params: {
      date: formatDateForApi(date),
      start,
      end,
    },
  });
  return response.data;
};

export const checkAvailability = validateAvailability;
export const validateBookingTime = validateAvailability;

export const createAvailabilityRule = async (payload) => {
  const response = await api.post(ENDPOINTS.AVAILABILITY.CREATE, {
    ...payload,
    date: formatDateForApi(payload?.date),
    startTime: payload?.startTime ? String(payload.startTime).slice(0, 5) : "",
    endTime: payload?.endTime ? String(payload.endTime).slice(0, 5) : "",
    isActive: payload?.isActive !== false,
  });

  return response.data;
};

export const addAvailabilityRule = createAvailabilityRule;

export const updateAvailabilityRule = async (id, payload) => {
  const response = await api.put(ENDPOINTS.AVAILABILITY.UPDATE(id), {
    ...payload,
    date: formatDateForApi(payload?.date),
    startTime: payload?.startTime ? String(payload.startTime).slice(0, 5) : "",
    endTime: payload?.endTime ? String(payload.endTime).slice(0, 5) : "",
    isActive: payload?.isActive !== false,
  });

  return response.data;
};

export const editAvailabilityRule = updateAvailabilityRule;

export const deleteAvailabilityRule = async (id) => {
  const response = await api.delete(ENDPOINTS.AVAILABILITY.DELETE(id));
  return response.data;
};

export const removeAvailabilityRule = deleteAvailabilityRule;

const availabilityService = {
  getAllAvailabilityRules,
  getAvailabilityRules,
  getAvailabilities,
  getAvailabilityRuleById,
  getAvailabilityByDate,
  getAvailabilityBetweenDates,
  validateAvailability,
  checkAvailability,
  validateBookingTime,
  createAvailabilityRule,
  addAvailabilityRule,
  updateAvailabilityRule,
  editAvailabilityRule,
  deleteAvailabilityRule,
  removeAvailabilityRule,
};

export default availabilityService;